/**
 * Form and message primitives shared by the integration panels.
 *
 * WHY THESE EXIST: every panel here is a form, and three of them show a result box.
 * Repeating the input classes by hand is how two fields end up a pixel apart.
 *
 * The result box comes from the shared `Callout`, re-exported here so a panel needs one import
 * for everything it renders.
 */
import type { ReactNode } from 'react';
import { HINT, LABEL } from '../common/ui';

/**
 * One input's worth of styling — used by every text field, select and textarea here. It is
 * ui.ts's INPUT box (bg-input, brand focus ring) sized by padding instead of `h-9`, so the
 * same class also fits a multi-line textarea.
 */
export const inputClass =
  'w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground ' +
  'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:cursor-not-allowed disabled:opacity-60';

/** Monospace variant for URLs, paths and tokens, where character shape matters. */
export const monoInputClass = inputClass.replace('text-[13px]', 'font-mono text-[12px]');

export interface FieldProps {
  label: string;
  /** Rendered under the control — what the value is for, not a repeat of the label. */
  hint?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, required, className = '', children }: FieldProps) {
  return (
    <div className={className}>
      <label className={LABEL}>
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      {children}
      {hint && <p className={HINT}>{hint}</p>}
    </div>
  );
}

/** A titled group of fields inside a long form, so a dialog reads as steps not a wall. */
export function FieldGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="rounded-xl border border-border bg-background p-4">
      <legend className="px-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </legend>
      <div className="space-y-3">{children}</div>
    </fieldset>
  );
}

export { Callout, StatusPill, type Tone as CalloutTone } from '../common/Callout';
